import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import * as Icons from 'assets/svg/sprite'
import { Tabs } from 'components/Tabs/Tabs'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { type ITicket } from 'pages/support/pages/Tickets/types/interfaces/ITicket'
import { searchTickets } from 'pages/support/service/endpoints/tickets'
import { useGetTickets } from 'pages/support/service/react-query/useGetTickets/useGetTickets'
import {
    filterTicketsByTab,
    useTicketsCacheStore
} from 'store/supportTicketsCacheStore'
import colors from 'styles/colors'

import { TicketWrapper } from './components/TicketWrapper'
import {
    TicketStatusSupport,
    TicketStatusZendesk
} from '../../types/enums/TicketStatus'

const SEARCH_MAX_AGE_MS = 2 * 60 * 1000
const PENDING_SYNC_POLL_MS = 2500

const useDebouncedSearch = (value: string, delay = 500) => {
    const [debouncedValue, setDebouncedValue] = useState(value)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value)
        }, delay)

        return () => {
            clearTimeout(handler)
        }
    }, [value, delay])

    return debouncedValue
}

export const TicketsList = () => {
    const { t } = useTranslation()

    const tabs = Object.values(TicketStatusZendesk).filter(
        status => status.toLowerCase() !== 'pending'
    )

    const [activeTab, setActiveTab] = useState(
        TicketStatusZendesk.ALL
    )

  const [searchTerm, setSearchTerm] = useState('')
  const [isSearching, setIsSearching] = useState(false)

  const cachedTickets = useTicketsCacheStore(
      state => state.tickets
  )

  const [allTickets, setAllTickets] = useState<ITicket[]>(
      () => cachedTickets ?? []
  )

  const debouncedSearchTerm = useDebouncedSearch(searchTerm)

    const setTicketsCache = useTicketsCacheStore(
        state => state.setTickets
    )

    const getSearchCache = useTicketsCacheStore(
        state => state.getSearchResults
    )

    const setSearchCache = useTicketsCacheStore(
        state => state.setSearchResults
    )

    const isSearchStale = useTicketsCacheStore(
        state => state.isSearchStale
    )

    /*
     * Immediately hydrate from Zustand so returning to this page
     * doesn't briefly show an empty/loading state.
     */
    useEffect(() => {
        if (
            !debouncedSearchTerm &&
            cachedTickets?.length
        ) {
            setAllTickets(cachedTickets)
        }
    }, [cachedTickets, debouncedSearchTerm])

    /*
     * Keep React Query enabled whenever we're viewing the normal
     * ticket list.
     *
     * This is important because invalidateQueries('tickets') after
     * creating, deleting or updating a ticket must actually be able
     * to trigger a refetch.
     */
    const {
        data: ticketsResponse,
        isLoading: loadingTickets,
        refetch: refetchTickets
    } = useGetTickets({
        enabled: !debouncedSearchTerm
    })

    /*
     * Whenever fresh server data arrives, it becomes the current
     * UI state and refreshes the instant Zustand cache.
     */
    useEffect(() => {
        if (
            !debouncedSearchTerm &&
            ticketsResponse?.message?.tickets
        ) {
            const fresh = ticketsResponse.message.tickets

            setTicketsCache(fresh)
            setAllTickets(fresh)
        }
    }, [
        ticketsResponse,
        debouncedSearchTerm,
        setTicketsCache
    ])

    /*
     * A newly-created ticket exists locally immediately while the
     * background Zendesk job is finishing.
     *
     * Poll only while one of those local tickets is waiting.
     * As soon as the backend replaces it with the Zendesk-backed
     * ticket, polling stops automatically.
     */
    const hasPendingSync = useMemo(() => {
        return allTickets.some(
            ticket =>
                ticket.zendesk_sync_status === 'pending'
        )
    }, [allTickets])

    useEffect(() => {
        if (
            debouncedSearchTerm ||
            !hasPendingSync
        ) {
            return
        }

        const interval = window.setInterval(() => {
            void refetchTickets()
        }, PENDING_SYNC_POLL_MS)

        return () => {
            window.clearInterval(interval)
        }
    }, [
        debouncedSearchTerm,
        hasPendingSync,
        refetchTickets
    ])

    /*
     * Search remains cache-first because it is separate from the
     * normal ticket-list query.
     */
    useEffect(() => {
        const fetchSearchResults = async () => {
            const term = debouncedSearchTerm.trim()

            if (!term) {
                /*
                 * Search was cleared.
                 *
                 * Restore the normal cached list immediately while
                 * React Query refreshes it in the background.
                 */
                if (cachedTickets) {
                    setAllTickets(cachedTickets)
                }

                return
            }

            const cached = getSearchCache(term)

            const canUseCache =
                cached?.results !== undefined &&
                !isSearchStale(
                    term,
                    SEARCH_MAX_AGE_MS
                )

            if (canUseCache) {
                setAllTickets(cached.results)

                return
            }

            setIsSearching(true)

            try {
                const results = await searchTickets(
                    1,
                    term
                )

                const next =
                    results?.message?.results ?? []

                setSearchCache(term, next)
                setAllTickets(next)
            } catch (error) {
                console.error(
                    'Search error:',
                    error
                )

                setAllTickets([])
            } finally {
                setIsSearching(false)
            }
        }

        void fetchSearchResults()
    }, [
        debouncedSearchTerm,
        cachedTickets,
        getSearchCache,
        isSearchStale,
        setSearchCache
    ])

    /*
     * Don't replace cached tickets with a loader when we already
     * have something useful to show.
     */
    const isLoading =
        isSearching ||
        (
            loadingTickets &&
            allTickets.length === 0
        )

    const filteredTickets = useMemo(() => {
        return filterTicketsByTab(
            allTickets,
            activeTab
        )
    }, [
        activeTab,
        allTickets
    ])

    /*
     * Keep this exactly as the existing behavior intended:
     * this count refers to actual Zendesk ticket status "pending",
     * NOT our background sync status.
     */
    const pendingTickets = useMemo(() => {
        return allTickets.filter(
            ticket =>
                ticket.status ===
                TicketStatusZendesk.PENDING
        )
    }, [allTickets])

    const handleSetActiveTab = (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        const button = e.currentTarget

        const label = button.innerText
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '')

        const matchingTab =
            Object.values(
                TicketStatusZendesk
            ).find(
                tab =>
                    tab.toLowerCase() ===
                    label
            )

        if (matchingTab) {
            setActiveTab(matchingTab)
        }
    }

    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='search-with-buttons'
                    searchValue={searchTerm}
                    searchFn={(term: string) =>
                        setSearchTerm(term)
                    }
                    clearFn={() =>
                        setSearchTerm('')
                    }
                    additionalProps={{
                        placeholder:
                            t('searchTickets')
                    }}
                    buttons={[]}
                />
            }
        >
<StyledTabsRow>
    <Tabs
        active={activeTab}
        tabs={tabs}
        tabWithNumber={TicketStatusSupport.OPEN}
        {...(pendingTickets.length > 0 && {
            number: pendingTickets.length
        })}
        handleOnClick={handleSetActiveTab}
    />

    {activeTab !== TicketStatusZendesk.CLOSED && (
        <StyledDesktopAddTicketLink
            to='/support/create-a-ticket'
        >
            <Icons.IconPlus color={colors.white} />
            <span>{t('createTicket')}</span>
        </StyledDesktopAddTicketLink>
    )}
</StyledTabsRow>

{activeTab !== TicketStatusZendesk.CLOSED && (
    <StyledAddTicketLink
        to='/support/create-a-ticket'
    >
        <Icons.IconPlus color={colors.white} />
    </StyledAddTicketLink>
)}

            <TicketWrapper
                tickets={filteredTickets ?? []}
                isLoading={isLoading}
            />
        </WithHeaderSection>
    )
}
const StyledTabsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding-right:1rem;
`

const StyledDesktopAddTicketLink = styled(Link)`
    align-items: center;
    background: ${colors.blue92deg};
    border: 0.2rem solid ${colors.blueLight};
    border-radius: 999px;
    box-shadow: 0 0.3rem 1rem rgba(47, 152, 254, 0.35);
    color: ${colors.white};
    display: flex;
    flex: 0 0 auto;
    gap: 0.6rem;
    height: 3.4rem;
    justify-content: center;
    padding: 0 1.6rem;
    text-decoration: none;
    font-size: 1.2rem;
    font-weight: 600;
    line-height: 1;

    svg {
        width: 1rem;
        height: 1rem;
    }

    @media (max-width: 768px) {
        display: none;
    }
`
const StyledAddTicketLink = styled(Link)`
    align-items: center;
    background: ${colors.blue92deg};
    border: 0.3rem solid ${colors.blueLight};
    border-radius: 16rem;
    box-shadow: 0 0.4rem 1.4rem rgba(47, 152, 254, 0.5);
    display: none;
    height: 6rem;
    justify-content: center;
    position: fixed;
    right: 2rem;
    bottom: 9rem;
    z-index: 10;
    width: 6rem;

    @media (max-width: 768px) {
        display: flex;
    }
`